import type { ReactNode } from 'react';
import type { Route } from '../lib/route';
import { href } from '../lib/route';

interface Props {
  readonly route: Route;
  readonly who: string;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}

interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly kinds: readonly Route['kind'][];
}

const NAV: readonly NavItem[] = [
  { label: 'Overview', to: '/', kinds: ['overview'] },
  { label: 'Accounts', to: '/accounts', kinds: ['accounts', 'account'] },
  { label: 'Frames', to: '/frames', kinds: ['frames', 'frame'] },
  { label: 'Photos', to: '/photos', kinds: ['photos'] },
  { label: 'Screens', to: '/screens', kinds: ['screens'] },
  { label: 'Albums', to: '/albums', kinds: ['albums'] },
  { label: 'Subscriptions', to: '/subscriptions', kinds: ['subscriptions'] },
  { label: 'Email', to: '/email', kinds: ['email'] },
  { label: 'Audit', to: '/audit', kinds: ['audit'] },
];

export const Shell = ({ route, who, onSignOut, children }: Props) => (
  <div className="console">
    <a className="skip" href="#main">
      Skip to content
    </a>
    <header className="console-head">
      <p className="masthead-id">
        Mantelpiece <span className="masthead-role">admin</span>
      </p>
      <p className="masthead-who">
        {who} ·{' '}
        <button type="button" className="linky-quiet" onClick={onSignOut}>
          Sign out
        </button>
      </p>
    </header>
    <nav className="console-nav" aria-label="Sections">
      <ul>
        {NAV.map((item) => {
          const current = item.kinds.includes(route.kind);
          return (
            <li key={item.to}>
              <a href={href(item.to)} aria-current={current ? 'page' : undefined}>
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
    <main id="main" className="console-main" tabIndex={-1}>
      {children}
    </main>
  </div>
);

interface PageHeadProps {
  readonly eyebrow?: string | undefined;
  readonly title: string;
  readonly lede?: ReactNode;
  readonly actions?: ReactNode;
}

export const PageHead = ({ eyebrow, title, lede, actions }: PageHeadProps) => (
  <div className="page-head">
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="title">{title}</h1>
      {lede && <p className="lede">{lede}</p>}
    </div>
    {actions && <div className="row-tight">{actions}</div>}
  </div>
);

export const Section = ({
  title,
  aside,
  children,
}: {
  readonly title: string;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) => (
  <section className="section">
    <div className="section-head">
      <h2 className="section-title">{title}</h2>
      {aside && <div className="section-aside">{aside}</div>}
    </div>
    {children}
  </section>
);
